const { CONSENT } = require('../../../common/js/constants/cookies');
const { getCookieOptions } = require('../../../common/js/universal/utils');
const { getNonEssentialCookies, getSanitizableCookies } = require('./cookies-get');
const { COOKIES_LIST, CONSENT_CATEGORIES } = require('./constants');

const sanitizer = require('./sanitizers');

const CONSENT_COOKIE = CONSENT;

const cookieManagerErrors = {
  missingLibrary: 'CookieManager: Missing argument. cookie library not found.',
  missingRemoveArguments: 'CookieManager: Missing argument. Name not found.',
  missingSetArguments: 'CookieManager: Missing argument. Name not found.',
};

const defaultSettings = {
  consentValidation: false,
};

class CookieManager {
  /**
   * Create CookieManager.
   * @param {Object} cookieLibrary - [cookie-universal | cookie-universal-nuxt | js-cookie]
   */
  constructor(cookieLibrary, settings = {}) {
    if (!cookieLibrary) {
      throw new Error(cookieManagerErrors.missingLibrary);
    }

    this.settings = Object.assign(defaultSettings, settings);

    this.cookieLibrary = cookieLibrary;
    this.consent = this.getConsent();
  }

  /**
   * Get the value of cookie with given name
   * @param {string} name - Name of the cookie
   * @param {Object} options - Cookie options
   * @return {string} The cookie value of given name.
   */
  get(name, options) {
    return this.cookieLibrary.get(name, options);
  }

  /**
   * Set given value of cookie with given name
   * @param {string} name - Name of the cookie
   * @param {string|number|boolean} value - Cookie value
   * @param {Object} options - Cookie options
   * @return cookie Library set return
   */
  set(name, value, options) {
    let cookieValue = value;

    if (!name) {
      throw new Error(cookieManagerErrors.missingSetArguments);
    }

    if (this.settings.consentValidation) {
      const cookieToSet = COOKIES_LIST
        .find((cookie) => cookie.name === name);

      if (!cookieToSet ||
          !this.consent.includes(cookieToSet.permission)) {
        this.remove(name, options);
        return false;
      }

      const cookieSanitizer = sanitizer[cookieToSet.sanitizer];

      // If a cookie has a sanitizer it is indicating
      // that it has different values which might need consent
      if (cookieSanitizer) {
        cookieValue = cookieSanitizer(this.consent, value);
      }
    }

    return this.cookieLibrary.set(name, cookieValue, options);
  }

  /**
   * Remove cookie with given name
   * @param {string} name - Name of the cookie
   * @param {Object} options - Cookie options
   * @return cookie Library remove return
   */
  remove(name, options) {
    if (!name) {
      throw new Error(cookieManagerErrors.missingRemoveArguments);
    }

    return this.cookieLibrary.remove(name, options);
  }

  /**
   * Accept all cookies
   * Sets permission to set all cookies
   */
  acceptAll() {
    const consent = Object.keys(CONSENT_CATEGORIES)
      .map((key) => CONSENT_CATEGORIES[key])
      .join('');

    this.setConsent(consent);
    this.applySnowplowSettings();
  }

  /**
   * Rejects non-essential cookies
   * Sets permission to only set essential cookies
   * and removes cookies/values with different consent
   */
  rejectNonEssential() {
    const consent = CONSENT_CATEGORIES.essential;
    this.setConsent(consent);
    this.removeNonEssentialCookies();
    this.resetNonEssentialValues();
    this.applySnowplowSettings();
  }

  /**
   * Removes non-essential cookies
   */
  removeNonEssentialCookies() {
    const nonEssential = getNonEssentialCookies();
    nonEssential.forEach((cookie) => this.cookieLibrary.remove(cookie.name));
  }

  /**
   * Resets non-essential cookie values
   */
  resetNonEssentialValues() {
    getSanitizableCookies().forEach((item) => {
      const cookieSanitizer = sanitizer[item.sanitizer];

      if (!cookieSanitizer) {
        return;
      }

      this.set(
        item.name,
        cookieSanitizer(this.consent, this.get(item.name)),
        getCookieOptions(),
      );
    });
  }

  /**
   * Applies snowplow related settings
   */
  applySnowplowSettings() {
    if (window && window.snowplow_ecosia) {
      if (this.consent.includes(CONSENT_CATEGORIES.analytics)) {
        window.snowplow_ecosia('disableAnonymousTracking');
      } else {
        window.snowplow_ecosia('enableAnonymousTracking', { withServerAnonymisation: true });
      }
    }
  }

  /**
   * Set cookie consent to given value
   * NOTE: Don't call setConsent directly
   * use either rejectNonEssential or acceptAll
   * @param {string} consent - New cookie consent
   */
  setConsent(consent) {
    this.consent = consent;
    this.cookieLibrary.set(CONSENT_COOKIE, consent, getCookieOptions());
  }

  /**
   * Return cookie consent from cookie
   * defaults to CONSENT_CATEGORIES.essential
   */
  getConsent() {
    return this.cookieLibrary.get(CONSENT_COOKIE, getCookieOptions()) ||
      CONSENT_CATEGORIES.essential;
  }

  /**
   * Set consent validation
   */
  setConsentValidation(validation) {
    this.settings.consentValidation = validation;
  }
}

module.exports = CookieManager;
module.exports.cookieManagerErrors = cookieManagerErrors;
